import type { Env, AuditRow } from "./types.js";
import { SCHEMA_GENERIC, withSchemaMarker } from "./audit-schema.js";

/**
 * Fire-and-forget Analytics Engine write.
 * Never await — AE writes are non-blocking and best-effort.
 *
 * Schema:
 *   blobs[0] = tool_name
 *   blobs[1] = ocs_method
 *   blobs[2] = status ('ok' | 'error' | 'scope_denied' | 'dry_run')
 *   blobs[3] = dry_run ('0' | '1')
 *   blobs[4] = sub (user email)
 *   blobs[5] = manus_task_id (empty when not a Manus dispatch)
 *   blobs[6] = manus_profile (empty when absent)
 *   blobs[7] = manus_key_used: "primary" | "fallback" (empty when absent)
 *   doubles[0] = duration_ms
 *   doubles[1] = ocs_status_code (0 = success, -1 = network error)
 *   indexes[0] = reseller_id (string; enables per-reseller filtering in SQL queries)
 *
 * Query example (Workers Analytics Engine SQL API):
 *   SELECT blob1 AS tool, SUM(_sample_interval) AS calls
 *   FROM carrier_mcp_audit
 *   WHERE timestamp > NOW() - INTERVAL '7' DAY
 *   GROUP BY tool ORDER BY calls DESC
 *
 * Note: use SUM(_sample_interval) not COUNT(*) — AE downsamples at high volume.
 */
export function writeAudit(
  env: Env,
  row: AuditRow & { sub: string; reseller_id: number },
): void {
  env.AUDIT_LOG.writeDataPoint({
    blobs: withSchemaMarker(
      [
        row.tool_name,
        row.ocs_method,
        row.status,
        row.dry_run ? "1" : "0",
        row.sub,
        row.manus_task_id ?? "",
        row.manus_profile ?? "",
        row.manus_key_used ?? "",
      ],
      SCHEMA_GENERIC,
    ),
    doubles: [row.duration_ms, row.ocs_status_code ?? 0],
    indexes: [String(row.reseller_id)],
  });
}
